import { UploadStatusError } from './sendFailureHelpers';

interface UploadImageProps {
  blob: Blob;
  filename: string;
  endpoint: string;
  token: string;
  onProgress?: (loaded: number, total: number, status: string) => void;
  onUploadComplete?: (response: Response) => void;
  onError?: (error: Error) => void;
}

const truncateErrorMessage = (message: string): string => {
  const maxLength = 2000;
  return message.length > maxLength
    ? `${message.slice(0, maxLength)}...`
    : message;
};

const formatErrorValue = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value;

  if (Array.isArray(value)) {
    return value
      .map(formatErrorValue)
      .filter((message): message is string => !!message)
      .join(', ');
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;

    if (record.errors) {
      if (Array.isArray(record.errors)) {
        const formatted = formatErrorValue(record.errors);
        if (formatted) return formatted;
      } else if (typeof record.errors === 'object' && record.errors !== null) {
        const errors = record.errors as Record<string, unknown>;
        const messages = Object.entries(errors)
          .map(([field, fieldErrors]) => {
            const formatted = formatErrorValue(fieldErrors);
            return formatted ? field + ': ' + formatted : undefined;
          })
          .filter((message): message is string => !!message);

        if (messages.length > 0) return messages.join(', ');
      }
    }

    return (
      formatErrorValue(record.detail) ||
      formatErrorValue(record.error) ||
      formatErrorValue(record.message) ||
      formatErrorValue(record.title)
    );
  }

  return undefined;
};

const getUploadErrorMessage = (xhr: XMLHttpRequest): string => {
  const fallback = `Upload failed with status ${xhr.status}`;
  const responseText = xhr.responseText?.trim();

  if (!responseText) {
    return xhr.statusText ? `${fallback} (${xhr.statusText})` : fallback;
  }

  try {
    const parsed = JSON.parse(responseText);
    const parsedMessage = formatErrorValue(parsed);
    if (parsedMessage)
      return `${fallback}: ${truncateErrorMessage(parsedMessage)}`;
  } catch {
    // Plain-text backend errors are expected for some upload failures.
  }

  return `${fallback}: ${truncateErrorMessage(responseText)}`;
};

export class ApiHelpers {
  static async uploadImage({
    blob,
    filename,
    endpoint,
    token,
    onProgress,
    onUploadComplete,
    onError,
  }: UploadImageProps) {
    try {
      // Create FormData and append the image
      const formData = new FormData();
      //   const blob = new Blob([imageData], { type: contentType });
      // const text = await blob.text();
      formData.append('image0', blob, filename);

      // Create upload request
      const xhr = new XMLHttpRequest();
      xhr.open('POST', endpoint, true);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      // xhr.setRequestHeader('Content-Type', 'multipart/form-data');

      // Track upload progress
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress?.(event.loaded, event.total, 'uploading');
        }
      };

      // Handle response
      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress?.(0, 0, 'completed');
          const response = new Response(xhr.response, {
            status: xhr.status,
            statusText: xhr.statusText,
            headers: new Headers({
              'Content-Type':
                xhr.getResponseHeader('Content-Type') || 'application/json',
            }),
          });
          onUploadComplete?.(response);
          // setProgress(prev => ({ ...prev, status: 'completed' }));
        } else {
          console.error(`Error uploading image: ${getUploadErrorMessage(xhr)}`);
          onError?.(new UploadStatusError(xhr.status));
          return;
        }
      };

      // Handle errors
      xhr.onerror = () => {
        onProgress?.(0, 0, 'error');
        console.error('Error uploading image: network error');
        onError?.(new Error('Network error occurred'));
      };

      // Start upload
      xhr.send(formData);
    } catch (error) {
      onProgress?.(0, 0, 'error');
      onError?.(
        error instanceof Error ? error : new Error('Unknown error occurred'),
      );
    }
  }
}
